import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://cvmcdnkfrfhhlzwlekab.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Service Realtime Usafiri — remplace Socket.io par Supabase Realtime Channels
 *
 * Channels utilisés :
 * - `order:{orderId}` — tracking position & statut d'une commande
 * - `user:{userId}` — notifications personnelles
 */
class RealtimeService {
  constructor() {
    this.channels = new Map();
    this.callbacks = new Map();
  }

  // ─── Authentification ─────────────────────────────────────────────────────────
  connect() {
    // Supabase Realtime se connecte automatiquement
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.id) {
      this.subscribeUser(user.id);
    }
  }

  disconnect() {
    this.channels.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    this.callbacks.clear();
  }

  // ─── Channel utilisateur (notifications) ──────────────────────────────────────
  subscribeUser(userId) {
    const channelName = `user:${userId}`;
    if (this.channels.has(channelName)) return;

    const channel = supabase.channel(channelName);
    channel
      .on('broadcast', { event: 'notification' }, (payload) => {
        const cb = this.callbacks.get('notification');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'delivery-offer' }, (payload) => {
        const cb = this.callbacks.get('delivery-offer');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'delivery-offer-cancelled' }, (payload) => {
        const cb = this.callbacks.get('delivery-offer-cancelled');
        if (cb) cb(payload.payload);
      })
      .subscribe();

    this.channels.set(channelName, channel);
  }

  // ─── Rejoindre la room d'une commande ─────────────────────────────────────────
  joinOrder(orderId) {
    const channelName = `order:${orderId}`;
    if (this.channels.has(channelName)) return;

    const channel = supabase.channel(channelName);
    channel
      .on('broadcast', { event: 'position-updated' }, (payload) => {
        const cb = this.callbacks.get('position-updated');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'order-status-changed' }, (payload) => {
        const cb = this.callbacks.get('order-status-changed');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'eta-changed' }, (payload) => {
        const cb = this.callbacks.get('eta-changed');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'delivery-code-assigned' }, (payload) => {
        const cb = this.callbacks.get('delivery-code-assigned');
        if (cb) cb(payload.payload);
      })
      .on('broadcast', { event: 'pickup-code-assigned' }, (payload) => {
        const cb = this.callbacks.get('pickup-code-assigned');
        if (cb) cb(payload.payload);
      })
      .subscribe();

    this.channels.set(channelName, channel);
  }

  leaveOrder(orderId) {
    const channelName = `order:${orderId}`;
    const channel = this.channels.get(channelName);
    if (channel) {
      supabase.removeChannel(channel);
      this.channels.delete(channelName);
    }
  }

  // ─── Envoyer la position du livreur ───────────────────────────────────────────
  sendPosition(orderId, position) {
    const channelName = `order:${orderId}`;
    const channel = this.channels.get(channelName);
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'position-updated',
        payload: {
          orderId,
          latitude: position.latitude,
          longitude: position.longitude,
          heading: position.heading || 0,
          speed: position.speed || 0,
        },
      });
    }
  }

  // ─── Envoyer un changement de statut ──────────────────────────────────────────
  sendStatusUpdate(orderId, status, customerId, deliveryInfo) {
    const channelName = `order:${orderId}`;
    const channel = this.channels.get(channelName);
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'order-status-changed',
        payload: { orderId, status, deliveryInfo, timestamp: Date.now() },
      });
    }

    // Aussi notifier le client via son channel perso
    if (customerId) {
      const userChannel = this.channels.get(`user:${customerId}`);
      if (userChannel) {
        userChannel.send({
          type: 'broadcast',
          event: 'notification',
          payload: {
            title: 'Mise à jour commande',
            message: this.getStatusMessage(status),
            type: 'order_status',
            orderId,
          },
        });
      }
    }
  }

  // ─── Envoyer mise à jour ETA ──────────────────────────────────────────────────
  sendEtaUpdate(orderId, eta, distance) {
    const channelName = `order:${orderId}`;
    const channel = this.channels.get(channelName);
    if (channel) {
      channel.send({
        type: 'broadcast',
        event: 'eta-changed',
        payload: { orderId, eta, distance },
      });
    }
  }

  // ─── Demander la position (pas nécessaire avec Realtime broadcast) ────────────
  requestPosition(orderId) {
    // Avec Supabase Realtime broadcast, le livreur envoie sa position en continu
    // Le client reçoit automatiquement via le listener
  }

  // ─── Callbacks ────────────────────────────────────────────────────────────────
  onPositionUpdate(callback) {
    this.callbacks.set('position-updated', callback);
  }

  onStatusChange(callback) {
    this.callbacks.set('order-status-changed', callback);
  }

  onEtaChange(callback) {
    this.callbacks.set('eta-changed', callback);
  }

  onNotification(callback) {
    this.callbacks.set('notification', callback);
  }

  onDeliveryOffer(callback) {
    this.callbacks.set('delivery-offer', callback);
  }

  onDeliveryOfferCancelled(callback) {
    this.callbacks.set('delivery-offer-cancelled', callback);
  }

  onDeliveryCodeAssigned(callback) {
    this.callbacks.set('delivery-code-assigned', callback);
  }

  off(event) {
    this.callbacks.delete(event);
  }

  removeAllOrderListeners() {
    this.callbacks.delete('position-updated');
    this.callbacks.delete('order-status-changed');
    this.callbacks.delete('eta-changed');
  }

  // ─── Utils ────────────────────────────────────────────────────────────────────
  getStatusMessage(status) {
    const messages = {
      confirmed: 'Votre commande a été confirmée !',
      preparing: 'Votre commande est en préparation',
      ready: 'Votre commande est prête !',
      picked_up: 'Le livreur a récupéré votre commande',
      delivering: 'Le livreur est en route vers vous',
      delivered: 'Votre commande a été livrée !',
      cancelled: 'Votre commande a été annulée',
    };
    return messages[status] || 'Statut de commande mis à jour';
  }
}

const realtimeService = new RealtimeService();
export default realtimeService;

// Export aussi le client Supabase pour d'autres usages
export { supabase };
